// Customer-safe text for any caught error. The shared rule (packages/shared/
// src/errors.ts) is that UI never renders developer text from the wire: a
// server `userMessage` is shown when it is specific, otherwise the translated
// copy for the error code; anything that is not an ApiError (network fault,
// timeout) gets the connection copy.
import { ApiError, copyForCode, errorCopy, fallbackErrorCopy } from '@wrapforce/shared/errors';
import i18next from '@/lib/i18n';

const GENERIC_BODIES = new Set<string>([...Object.values(errorCopy).map((c) => c.body), fallbackErrorCopy.body]);

export function apiErrorMessage(err: unknown): string {
  if (err instanceof ApiError) {
    if (err.message && !GENERIC_BODIES.has(err.message)) return err.message;
    return i18next.t(`errors.${err.code}.body`, { ns: 'common', defaultValue: copyForCode(err.code).body });
  }
  return i18next.t('errors.connectionHiccup.body', { ns: 'common' });
}

// Supabase auth errors (sign in, sign up, codes, password changes) carry a
// stable `code`; the raw English `message` is never shown.
const AUTH_CODE_KEYS: Record<string, string> = {
  invalid_credentials: 'invalidCredentials',
  email_not_confirmed: 'emailNotConfirmed',
  user_already_exists: 'userAlreadyExists',
  email_exists: 'userAlreadyExists',
  weak_password: 'weakPassword',
  same_password: 'samePassword',
  otp_expired: 'otpExpired',
  otp_disabled: 'otpExpired',
  over_email_send_rate_limit: 'rateLimited',
  over_request_rate_limit: 'rateLimited',
  over_sms_send_rate_limit: 'rateLimited',
  email_address_invalid: 'emailInvalid',
  validation_failed: 'emailInvalid',
  signup_disabled: 'signupDisabled',
  user_not_found: 'invalidCredentials',
  session_expired: 'sessionExpired',
  refresh_token_not_found: 'sessionExpired',
};

export function authErrorMessage(err: unknown, fallback: string): string {
  const code = typeof err === 'object' && err !== null && 'code' in err ? String((err as { code?: unknown }).code ?? '') : '';
  const key = AUTH_CODE_KEYS[code];
  if (key) return i18next.t(`authErrors.${key}`, { ns: 'auth' });
  if (err instanceof TypeError) return i18next.t('errors.connectionHiccup.body', { ns: 'common' });
  return fallback;
}
